import { inject, InjectionToken } from '@angular/core';
import {
  MutationCache,
  QueryCache,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { ERROR_DETAILS_KEY } from '../consts/error.consts';
import type { EnqueueSnackbarConfiguration } from '../models/snackbar.models';
import {
  getLogError,
  isTanStackMeta,
  normalizeError,
  resolveLogPayload,
  type LogIfNeededPayload,
  type ResolveSnackbarConfigurationPayload,
} from '../models/tanstack-query-client.models';
import { LoggerService, MessageLevel } from '../services/logger.service';
import { SnackbarService } from '../services/snackbar.service';

/**
 * Resolves the snackbar configuration based on the provided payload.
 *
 * The function determines the snackbar configuration by inspecting the `meta` object
 * and evaluating the snackbar-specific metadata (`errorSnackbar` or `successSnackbar`)
 * depending on the `kind` provided. If the metadata is undefined or does not conform
 * to the expected structure, the function returns `undefined`.
 *
 * The snackbar configuration may either be directly defined or be a function that
 * generates the configuration based on the provided `variables` and `data`.
 *
 * @param {ResolveSnackbarConfigurationPayload} payload - The input payload containing
 *                                                         meta information, kind,
 *                                                         variables, and data.
 * @param {unknown} payload.meta - Metadata object that may contain snackbar-related fields.
 * @param {string} payload.kind - Indicates the type of snackbar ("error" or "success").
 * @param {unknown} payload.variables - Variables passed to the snackbar configuration function.
 * @param {unknown} [payload.data] - Data passed to a success snackbar configuration function.
 * @param {unknown} [payload.error] - Error passed to an error snackbar configuration function.
 *
 * @returns {EnqueueSnackbarConfiguration|undefined} - The snackbar configuration object
 *                                                     or undefined if the configuration
 *                                                     cannot be resolved.
 */
const resolveSnackbarConfiguration = (
  payload: ResolveSnackbarConfigurationPayload,
): EnqueueSnackbarConfiguration | undefined => {
  if (!isTanStackMeta(payload.meta)) {
    return undefined;
  }

  if (payload.kind === 'error') {
    const errorSnackbar = payload.meta.errorSnackbar;

    if (errorSnackbar === undefined) {
      return undefined;
    }

    return typeof errorSnackbar === 'function' ?
        errorSnackbar({
          variables: payload.variables,
          error: payload.error,
        })
      : errorSnackbar;
  }

  const successSnackbar = payload.meta.successSnackbar;

  if (successSnackbar === undefined) {
    return undefined;
  }

  return typeof successSnackbar === 'function' ?
      successSnackbar({
        variables: payload.variables,
        data: payload.data,
      })
    : successSnackbar;
};

/**
 * Logs an error if conditions are met by normalizing and resolving payload details.
 *
 * @param {LogIfNeededPayload} payload - The payload for logging.
 * @param {unknown} payload.meta - Metadata that defines whether to log the error.
 * @param {unknown} payload.error - The error to be logged.
 * @param {LogIfNeededPayload['extra]} payload.extra - Additional details to include in the log payload.
 * @param {LoggerService} payload.loggerService - The logging service used to perform the log action.
 *
 * @returns {void} This function doesn't return any value.
 */
const logIfNeeded = ({
  meta,
  error,
  extra,
  loggerService,
}: LogIfNeededPayload): void => {
  const logError = getLogError(meta);
  if (logError === undefined) {
    return;
  }

  const normalisedError = normalizeError(error);
  const payload = resolveLogPayload(logError, { error, ...extra });

  const name = payload.name ?? normalisedError.name;

  loggerService.log({
    level: MessageLevel.Error,
    message: payload.message ?? name,
    data: {
      [ERROR_DETAILS_KEY]: {
        ...payload.data,
      },
    },
  });
};

/**
 * Creates a custom `QueryClient` instance with configured `QueryCache` and `MutationCache`
 * to handle query and mutation events. The generated client integrates with
 * `SnackbarService` and `LoggerService` to display user notifications and log
 * events as appropriate.
 *
 * @param {SnackbarService} snackbarService - Service to display success or error messages to users.
 * @param {LoggerService} loggerService - Service to log information, warnings, or errors.
 *
 * @returns {QueryClient} Configured QueryClient instance.
 */
const createQueryClient = (
  snackbarService: SnackbarService,
  loggerService: LoggerService,
): QueryClient => {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        const configuration = resolveSnackbarConfiguration({
          meta: query.meta,
          kind: 'error',
          variables: undefined,
          error,
        });

        if (configuration !== undefined) {
          snackbarService.enqueueError(configuration);
        }

        logIfNeeded({
          loggerService,
          error,
          meta: query.meta,
          extra: { query },
        });
      },

      onSuccess: (data, query) => {
        const configuration = resolveSnackbarConfiguration({
          meta: query.meta,
          kind: 'success',
          variables: undefined,
          data,
        });

        if (configuration === undefined) {
          return;
        }

        snackbarService.enqueueSuccess(configuration);
      },
    }),

    mutationCache: new MutationCache({
      // eslint-disable-next-line @typescript-eslint/max-params
      onError: (error, variables, context, mutation) => {
        const configuration = resolveSnackbarConfiguration({
          meta: mutation.meta,
          kind: 'error',
          variables,
          error,
        });

        if (configuration !== undefined) {
          snackbarService.enqueueError(configuration);
        }

        logIfNeeded({
          loggerService,
          error,
          meta: mutation.meta,
          extra: {
            mutation,
            variables,
            context,
          },
        });
      },

      // eslint-disable-next-line @typescript-eslint/max-params
      onSuccess: (data, variables, _context, mutation) => {
        const configuration = resolveSnackbarConfiguration({
          meta: mutation.meta,
          kind: 'success',
          variables,
          data,
        });

        if (configuration === undefined) {
          return;
        }
        snackbarService.enqueueSuccess(configuration);
      },
    }),
    // NOTE: Should we always by default retry queries and mutations BE calls?
    defaultOptions: {
      queries: {
        retry: false,
      },
      mutations: {
        retry: false,
      },
    },
  });
};

/**
 * Injection token used to provide an instance of `QueryClient` throughout the application.
 * This token is generated using the Angular `InjectionToken` class and includes a factory function
 * for creating the `QueryClient` instance.
 *
 * The factory function injects the `SnackbarQueueService` and `LoggerService` dependencies,
 * which are required to construct the `QueryClient`. The resulting instance is configured
 * with these services to enable necessary functionality such as feedback notifications
 * and logging within the application's query management.
 */
export const APP_QUERY_CLIENT = new InjectionToken<QueryClient>(
  'APP_QUERY_CLIENT',
  {
    factory: (): QueryClient => {
      const snackbarService = inject(SnackbarService);
      const loggerService = inject(LoggerService);
      return createQueryClient(snackbarService, loggerService);
    },
  },
);
